/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from 'react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import Grid from '@mui/material/Grid';

import WidgetDialog from '../components/dialogs';
import TitleHeader from '../components/titleHeader';
import CalendarsList from './calendarList';
import calendarsLinks, {
    calendarsForAndroid,
    calendarsForIOS,
    calendarsForMacAndIPad,
    calendarsForNonMac,
} from './calendarIconLinks';

import { useStyles } from './styles';

import useWidgetTheme from '../../commons/widgetTheme';
import TranslationIds from '../../commons/translationIds';
import TranslationVarProps from '../../commons/translationIds';
import useTranslator from '../../commons/translations';

import ContentContainer from '../components/contentContainer';
import ErrorMsgBox from '../components/errorMsgBox';

import { useAppStore } from '../../store/models/app';
import usePageTransitions from '../../store/models/animations/usePageTransitions';
import useErrors from '../../store/models/errors/useErrors';
import transitions, 
{
    TransitionDirection,
    TransitionViewNames,
    TransitionViewRenderers,
} from '../../store/models/animations/actions/transitions';
import useKickstarted from '../../commons/hooks/useKickstarted';
import useUIStates from '../../store/models/uistates/useUIStates';

import { 
    getCalendarName,
 } from '../../commons/utils/utils';

function Calendars (props) {
    const {
        viewAnimation = false,
        initStyles = {},
    } = props;
    const appStore = useAppStore();

    const [calendars, setCalendars] = useState(calendarsLinks);
    const [isPreview] = useState(appStore.isPreview);
    const  {
        translate,
        translateWithValues,
    } = useTranslator();

    const {
        selectedCalendar,
    } = useUIStates();

    const {
        widget,
        widgetTheme,
    }  = useWidgetTheme();

    const classes = useStyles({
        widget,
        widgetTheme,
        viewAnimation,
    });

    const {
        setTransitionsWithTarget,
        setViewRenderer,
        setTransitionsProps,
    } = usePageTransitions({
        elementName: getName(),
        viewAnimation,
    });

    useKickstarted({
        onKickstarted: () => {
            updateCalendars();
        },
    });

    function updateCalendars () {
        // Handle desktop devices
        if (appStore.flags.desktop) {
            if (appStore.flags.mac) {
                setCalendars(calendarsForMacAndIPad());
                return;
            }
            setCalendars(calendarsForNonMac());
            return;
        }

        // Handle iOS devices
        if (appStore.flags.ios) {
            // Handle iPad (iOS but not mobile) 
            if (!appStore.flags.mobile) {
                setCalendars(calendarsForMacAndIPad());
                return;
            }

            // Handle iPhone (iOS and mobile)
            setCalendars(calendarsForIOS());
            return;
        }

        // Handle Android devices
        if (appStore.flags.android) {
            setCalendars(calendarsForAndroid());
            return;
        }

        // Handle other devices
        setCalendars(calendarsForMacAndIPad());
    }

    const { error } = useErrors();

    function extraProps () {
        if (viewAnimation) {
            return {
                'data-animation-view': 'for-animation',
            };
        }

        return {};
    }

    function transitionToBack () {
        setViewRenderer(TransitionViewRenderers.SCHEDULES_VIEW);
        setTransitionsWithTarget({
            direction: TransitionDirection.BACK,
            animationIn: transitions.slideInFromBack,
            animationOut: transitions.slideOutToNext,
            incomingView: TransitionViewNames.SCHEDULES_VIEW,
            outgoingView: TransitionViewNames.CALENDARS_VIEW,
        });
        appStore.gotoSchedules();
    }

    function getName () {
        if (viewAnimation) {
            return TransitionViewNames.CALENDARS_VIEW;
        }
        return TransitionViewNames.CALENDARS_VIEW;
    }

    function checkIfShowBackButton () {
        if (widget?.skip_schedule_selection ||
            appStore.autoSubscribe ||
            isPreview
        ) {
            return false;
        }

        return true;
    }

    function renderTransitionContent () {
        return (
            <WidgetDialog
                name={getName()}
                isShowBackButton={checkIfShowBackButton()}
                isShowCloseButton={!isPreview}
                goToBackPage={transitionToBack}
                viewAnimation={viewAnimation}
                initStyles={initStyles}
                { ...extraProps() }
            >
                <Grid className={classes.content}>
                    <Grid className={classNames([classes.headerSpace])}>
                        <TitleHeader
                            widget={widget}
                            widgetTheme={widgetTheme}
                            title={translate(TranslationIds.CALENDAR_HEADER)}
                            subtitle={translate(TranslationIds.CALENDAR_FOOTER)}
                            termsValue={translate(TranslationVarProps.CALENDAR_TERM_FOOTER)}
                            privacyValue={translate(TranslationVarProps.PRIVACY_FOOTER)}
                        />
                    </Grid>
                    { 
                        !isEmpty(error) && 
                        <ErrorMsgBox 
                            title={translate(TranslationIds.CALENDAR_ALERT_INFO_TITLE)}
                            message={translateWithValues(TranslationIds.CALENDAR_ALERT_INFO_CONTENT, { calendar: getCalendarName(selectedCalendar) })}
                            refId={error.refId}
                        /> 
                    }
                    <Grid className={classNames([classes.calendarListPanel])}> 
                        <CalendarsList
                            calendars={calendars}
                            isPreview={isPreview}
                            onCalendarSelect={() => {
                                setTransitionsProps({
                                    direction: TransitionDirection.NEXT,
                                    animationIn: transitions.slideInFromNext,
                                    animationOut: transitions.slideOutToBack,
                                });
                            }}
                        />
                    </Grid>
                </Grid>
            </WidgetDialog>
        );
    }

    if (!appStore.kickstarted) {
        return (<React.Fragment></React.Fragment>);
    }

    return (
        <React.Fragment>
            <ContentContainer
                viewAnimation={viewAnimation}
            >
             { renderTransitionContent() }
            </ContentContainer>          
        </React.Fragment>
    );
}

Calendars.propTypes = {
    viewAnimation: PropTypes.bool,
    initStyles: PropTypes.any,
};

export default observer(Calendars);